/**
 * SanPlumb — Controles del visor 3D (Fase 3d)
 *
 * Overlays flotantes sobre el canvas:
 *   - ViewControls: presets de cámara (ISO/TOP/ELEV/LAT)
 *   - LayerToolbar: toggles de capas (sanitarios/planos/muros/medidas)
 *   - RegenButton: botón "Regenerar 3D" con la estrategia activa
 *
 * Portado de `screens.jsx` / `viewer3d` del diseño aprobado.
 */

import type { CameraView, LayerState } from '../../types/ui';
import { Icon } from '../Icon';

// ── ViewControls ────────────────────────────────────────────────────

const VIEWS: readonly { id: CameraView; label: string }[] = [
  { id: 'ISO', label: 'ISO' },
  { id: 'TOP', label: 'PLANTA' },
  { id: 'ELEV', label: 'ELEV' },
  { id: 'LAT', label: 'LATERAL' },
];

export function ViewControls({
  view,
  onView,
  autoRotate,
  onToggleAutoRotate,
}: {
  view: CameraView;
  onView: (v: CameraView) => void;
  autoRotate: boolean;
  onToggleAutoRotate: () => void;
}): JSX.Element {
  return (
    <div className="view-bar">
      {VIEWS.map((v) => (
        <button
          key={v.id}
          className={`view-btn ${view === v.id ? 'active' : ''}`}
          onClick={() => onView(v.id)}
        >
          {v.label}
        </button>
      ))}
      <button
        className={`view-btn view-btn-icon ${autoRotate ? '' : 'active'}`}
        onClick={onToggleAutoRotate}
        title={autoRotate ? 'Pausar auto-rotación' : 'Reanudar auto-rotación'}
        aria-label={autoRotate ? 'Pausar auto-rotación' : 'Reanudar auto-rotación'}
      >
        {autoRotate ? Icon.pause : Icon.play}
      </button>
    </div>
  );
}

// ── LayerToolbar ────────────────────────────────────────────────────

interface LayerDef {
  key: keyof LayerState;
  short: string;
  label: string;
  color: string;
}

const LAYERS: readonly LayerDef[] = [
  { key: 'showAF', short: 'AF', label: 'Agua fría', color: 'var(--af)' },
  { key: 'showAC', short: 'AC', label: 'Agua caliente', color: 'var(--ac)' },
  { key: 'sanitarios', short: 'SAN', label: 'Sanitarios', color: 'var(--t-sec)' },
  { key: 'planos', short: 'NPT', label: 'Planos NPT', color: 'var(--acc-af)' },
  { key: 'muros', short: 'MUR', label: 'Muros', color: 'var(--af)' },
  { key: 'labelsMedidas', short: 'MED', label: 'Medidas', color: 'var(--dim)' },
];

export function LayerToolbar({
  layers,
  onToggle,
}: {
  layers: LayerState;
  onToggle: (k: keyof LayerState) => void;
}): JSX.Element {
  return (
    <div className="layer-toolbar">
      <div className="lt-title">Capas</div>
      {LAYERS.map((l) => {
        const on = layers[l.key];
        return (
          <button
            key={l.key}
            className={`lt-btn ${on ? 'on' : 'off'}`}
            onClick={() => onToggle(l.key)}
          >
            <span className="lt-bullet" style={on ? { background: l.color, borderColor: l.color } : undefined} />
            <span className="lt-short">{l.short}</span>
            <span className="lt-label">{l.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── RegenButton ─────────────────────────────────────────────────────

export function RegenButton({
  routeLabel,
  onRegen,
}: {
  routeLabel: string;
  onRegen: () => void;
}): JSX.Element {
  return (
    <button className="regen-btn" onClick={onRegen}>
      {Icon.regen}
      <span className="rb-label">Regenerar 3D</span>
      <span className="rb-route">{routeLabel}</span>
    </button>
  );
}
